import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MDTypography from 'components/MDTypography';
import { useNavigate } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SmallStatusLabel from 'components/SmallStatusLabel/SmallStatusLabel';
import {
  Box,
  Card,
  Paper,
  SvgIcon,
  IconButton,
  Grid,
  Typography,
  Button,
  Checkbox,
  CircularProgress,
  Tooltip,
} from '@mui/material'; // Add Checkbox, CircularProgress
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CustomSnackbar from 'components/CustomSnackbar';
import { ControllerCard } from '../../components/Controllers/ControllerCard';
import SensorsIcon from '@mui/icons-material/Sensors';
import PublicIcon from '@mui/icons-material/Public';
import CircleIcon from '@mui/icons-material/Circle';
import {
  fetchUserSchedules,
  fetchSchedulesDetails,
  deleteScheduleById,
  fetchControllers,
  activateScheduleForDevices,
  activateScheduleForSingleDevice,
  activateScheduleByScheduleId,
  changeActiveScheduleId,
} from 'stores/sensor/controllersSlice';
import { Margin } from '@mui/icons-material';
import { brandColors } from 'types/colors';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const PLAY_BTN_STYLE = {
  backgroundColor: '#fff',
  boxShadow: `0 0 0 ${brandColors.GREY}00`,
  color: brandColors.GREEN,
  border: `1px solid ${brandColors.GREEN}`,
  '&:hover': {
    backgroundColor: brandColors.GREEN,
    color: '#fff',
    boxShadow: '0 0 6px rgba(60, 179, 113, 0.7)',
    border: `1px solid ${brandColors.GREEN}`,
  },
};

const getScheduleDeviceIds = props => {
  if (Array.isArray(props.devices)) {
    return props.devices.map(device => device.id).filter(Boolean);
  }
  return props.device_ids || [];
};

const getDeviceLocation = deviceDetails => {
  return deviceDetails.map((device, idx) => (
    <Box key={device.id} sx={{}}>
      <Box sx={{ display: 'flex', gap: '.6rem', alignItems: 'center' }}>
        <SensorsIcon sx={{ scale: '1.5' }} />
        <MDTypography
          variant="body2"
          sx={{
            fontSize: '0.95rem',
            fontWeight: 600,
            color: 'text.primary',
          }}>
          {device.name}
        </MDTypography>
      </Box>
      <MDTypography
        variant="body2"
        sx={{
          fontSize: '0.875rem',
          color: 'text.secondary',
        }}>
        {device.building} / Floor {device.floor} / {device.area}
      </MDTypography>
    </Box>
  ));
};

export const ScheduleCard = props => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [isActivating, setIsActivating] = useState(false);
  const filteredControllers = useSelector(s => s.controllers.controllers || []);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const scheduleDeviceIds = useMemo(() => getScheduleDeviceIds(props), [props.devices, props.device_ids]);

  // Find device details if this is a recommendation
  const deviceDetails = useMemo(() => {
    // if (!props.is_auto_generated || !props.device_ids?.length) return [];
    if (props.is_global) return [];

    return scheduleDeviceIds
      .map(deviceId => {
        const device = filteredControllers.find(d => String(d.id) === String(deviceId));
        return device
          ? {
              id: device.id,
              name: device.name,
              building: device.building_name || device.building,
              floor: device.floor_number,
              area: device.area_name,
            }
          : null;
      })
      .filter(Boolean);
  }, [props.is_auto_generated, props.is_global, scheduleDeviceIds, filteredControllers]);

  const assignedDeviceIds = useMemo(() => scheduleDeviceIds.map(id => String(id)), [scheduleDeviceIds]);
  const assignedControllers = useMemo(
    () => filteredControllers.filter(controller => assignedDeviceIds.includes(String(controller.id))),
    [filteredControllers, assignedDeviceIds],
  );

  const primaryDevice = useMemo(() => {
    if (deviceDetails.length > 0) return deviceDetails[0];
    const deviceId = scheduleDeviceIds[0];
    if (!deviceId) return null;
    const controller = filteredControllers.find(d => String(d.id) === String(deviceId));
    if (!controller) return null;
    return {
      id: controller.id,
      name: controller.name,
      building: controller.building_name || controller.building,
      floor: controller.floor_number,
      area: controller.area_name,
    };
  }, [deviceDetails, filteredControllers, scheduleDeviceIds]);

  // Find the controller names for this schedule. React reuses (useMemo) this list until
  // the assigned controllers or schedule ID changes.
  const activeControllerNames = useMemo(
    () =>
      assignedControllers
        .filter(controller => String(controller.active_schedule_id) === String(props.scheduleId))
        .map(controller => controller.name || `Controller ${controller.id}`),
    [assignedControllers, props.scheduleId],
  );

  const activeOnCount = activeControllerNames.length;

  // Show up to three names on the card. Put the remaining amount after "+".
  const activeControllerNamesPreview =
    activeOnCount <= 3
      ? activeControllerNames.join(', ')
      : `${activeControllerNames.slice(0, 3).join(', ')} +${activeOnCount - 3} more`;
  const isActiveForAssignedDevice = useMemo(() => {
    if (props.is_global) return false;
    if (!assignedControllers.length) return false;
    return assignedControllers.some(controller => String(controller.active_schedule_id) === String(props.scheduleId));
  }, [props.is_global, assignedControllers, props.scheduleId]);

  const handleScheduleEdit = () => {
    navigate(`/manage-schedules/${props.scheduleId}`);
  };

  const handleScheduleDelete = async (scheduleId, scheduleName) => {
    if (!window.confirm(`Are you sure you want to delete "${scheduleName}"?`)) {
      return;
    }

    try {
      await dispatch(deleteScheduleById(scheduleId)).unwrap();
      setSnack({
        open: true,
        message: `Schedule "${scheduleName}" deleted successfully`,
        severity: 'success',
      });
      await dispatch(fetchSchedulesDetails());

      // If deleted schedule was active, clear it
      if (activeScheduleId === scheduleId) {
        dispatch(changeActiveScheduleId(null));
      }
    } catch (error) {
      setSnack({
        open: true,
        message: `Failed to delete schedule: ${error.message}`,
        severity: 'error',
      });
    }
  };

  const handleScheduleActivate = async () => {
    if (props.is_global) {
      dispatch(fetchControllers());
      setShowDeviceModal(true); // Open the modal
    } else {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmActivation = async () => {
    // if (selectedDevices.length === 0) return;

    setIsActivating(true);

    try {
      const cleanDeviceIds = [...new Set(selectedDevices.map(id => String(id)))];
      await dispatch(
        activateScheduleForDevices({
          scheduleId: props.scheduleId,
          devicesIds: cleanDeviceIds,
        }),
      ).unwrap();

      await dispatch(fetchSchedulesDetails());
      await dispatch(fetchControllers());
      // Update active schedule in Redux
      dispatch(changeActiveScheduleId(props.scheduleId));

      // Show success message
      setSnack({
        open: true,
        message: `Schedule "${props.scheduleName}" activated on ${selectedDevices.length} device(s)`,
        severity: 'success',
      });

      // Close modal and reset
      setShowDeviceModal(false);
      setSelectedDevices([]);
    } catch (error) {
      setSnack({
        open: true,
        message: `Failed to activate schedule: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsActivating(false);
    }
  };

  const handleConfirmSingleActivation = async () => {
    setIsActivating(true);

    try {
      const deviceId = scheduleDeviceIds[0];
      if (deviceId) {
        await dispatch(
          activateScheduleForSingleDevice({
            scheduleId: props.scheduleId,
            deviceId,
          }),
        ).unwrap();
        await dispatch(fetchControllers());
      } else {
        setSnack({
          open: true,
          message: `There is no device linked to this schedule`,
          severity: 'error',
        });
        return;
      }

      await dispatch(fetchSchedulesDetails());

      dispatch(changeActiveScheduleId(props.scheduleId));

      setSnack({
        open: true,
        message: `Schedule "${props.scheduleName}" activated`,
        severity: 'success',
      });

      setShowConfirmModal(false);
    } catch (error) {
      setSnack({
        open: true,
        message: `Failed to activate schedule: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsActivating(false);
    }
  };

  const toggleDeviceSelection = deviceId => {
    const normalizedId = Number(deviceId);
    setSelectedDevices(prev => {
      if (prev.includes(normalizedId)) {
        return prev.filter(id => id !== normalizedId);
      }
      return [...prev, normalizedId];
    });
  };

  useEffect(() => {
    if (showDeviceModal) {
      // Initialize with devices that already have this schedule active
      const normalizedIds = assignedControllers
        .filter(controller => String(controller.active_schedule_id) === String(props.scheduleId))
        .map(controller => Number(controller.id));
      setSelectedDevices(normalizedIds);
    }
  }, [showDeviceModal, assignedControllers, props.scheduleId]);

  const globalLabel = props.is_global ? 'global' : '1';

  return (
    <>
      <Card
        onClick={handleScheduleEdit}
        sx={{
          my: 1,
          // pb: 1,
          width: {
            xs: '325px',
            '&:hover': {
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out',
            },
            transition: 'all 0.2s ease-in-out',
          },
        }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          ml={1}
          mr={1.25}
          my={-0.5}
          sx={{
            padding: '12px 0px 4px 0px',
          }}>
          <Box
            className="scheduleName"
            sx={{
              display: 'flex',
              width: '19.5rem',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Paper
                  className="sensorStatusIcon"
                  color={'white'}
                  elevation={1}
                  style={{
                    background: 'blue',
                  }}
                  sx={{
                    padding: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '3rem',
                    maxWidth: '3rem',
                    height: '3rem',
                  }}>
                  <CalendarMonthIcon
                    sx={{
                      color: '#fff',
                      scale: '2',
                      '&:hover': {
                        color: '#fffff',
                      },
                    }}
                  />
                </Paper>
                <MDTypography
                  fontSize={16}
                  sx={{
                    overflowWrap: 'anywhere',
                    marginLeft: '1rem',
                    fontWeight: 'bold',
                    color: '#000',
                  }}>
                  {props.scheduleName}
                </MDTypography>
              </Box>

              {props.is_auto_generated && (
                <Tooltip
                  arrow
                  placement="right-start"
                  title="This schedule was created using the generate recommendation method."
                  slotProps={{
                    tooltip: {
                      sx: {
                        minWidth: '20rem',
                        maxWidth: '20rem',
                        '& .MuiTooltip-arrow': {
                          left: '-.6rem',
                          // right: 'auto',
                        },
                      },
                    },
                  }}>
                  <Box onClick={e => e.stopPropagation()} sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                    <AutoAwesomeIcon sx={{ color: '#000' }} fontSize="small" />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '7px 12px',
            fontSize: '16px',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', variant: 'body2', width: '100%' }}>
            {/* Status indicator */}
            {/* Status:
            <MDTypography
              variant="body2"
              fontWeight="bold"
              color={props.status === 'active' || props.status === 'active_not_synced' ? 'success' : 'text'}>
              {props.status === 'active_not_synced' ? 'Active' : props.status}
            </MDTypography> */}
            {/* Show device details for recommendations */}
            {props.is_auto_generated && deviceDetails.length > 0 ? (
              <Box sx={{ width: '100%' }}>{getDeviceLocation(deviceDetails)}</Box>
            ) : (
              <>
                {props.is_global ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Box>
                      <MDTypography variant="body2">
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          Available
                        </Box>{' '}
                        on{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {props.device_count}
                        </Box>{' '}
                        {props.device_count === 1 ? 'device' : 'devices'}
                      </MDTypography>
                      <MDTypography variant="body2">
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          Active
                        </Box>{' '}
                        on{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {activeOnCount}
                        </Box>{' '}
                        {activeOnCount === 1 ? 'device' : 'devices'}
                      </MDTypography>
                      {/* Hide this line when there are no names. Hover over it to see the full list. */}
                      {activeControllerNames.length > 0 && (
                        <Tooltip
                          title={activeControllerNames.map((name, index) => (
                            <Box key={`${name}-${index}`}>{name}</Box>
                          ))}
                          placement="top"
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                maxWidth: 'none',
                                minWidth: '14rem',
                                whiteSpace: 'nowrap',
                              },
                            },
                          }}>
                          <MDTypography
                            variant="caption"
                            onClick={e => e.stopPropagation()}
                            sx={{
                              display: 'block',
                              // Keep long names on one line and end hidden text with "...".
                              maxWidth: '13rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'text.secondary',
                              lineHeight: 1.4,
                              cursor: 'default',
                            }}>
                            {activeControllerNamesPreview}
                          </MDTypography>
                        </Tooltip>
                      )}
                    </Box>
                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-end' }}>
                      <IconButton
                        size="medium"
                        sx={PLAY_BTN_STYLE}
                        onClick={e => {
                          e.stopPropagation();
                          handleScheduleActivate();
                        }}
                        title="Activate">
                        <PlayArrowIcon fontSize="16px" />
                      </IconButton>

                      {!['active', 'active_not_synced'].includes(props.status) ||
                        (activeOnCount === 0 && (
                          <IconButton
                            size="medium"
                            sx={{
                              border: `1px solid ${brandColors.GREY}`,
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                              },
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              handleScheduleDelete(props.scheduleId, props.scheduleName);
                            }}
                            title="Delete">
                            <DeleteIcon fontSize="16px" />
                          </IconButton>
                        ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ width: '100%' }}>{getDeviceLocation(deviceDetails)}</Box>
                )}
              </>
            )}
          </Box>
        </Box>
        {!props.is_global && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingX: '12px',
              alignItems: 'flex-end',
              padding: '7px 12px',
            }}>
            <SmallStatusLabel statusLabel={isActiveForAssignedDevice ? 'ACTIVE' : 'INACTIVE'} />
            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-end' }}>
              {!isActiveForAssignedDevice && (
                <IconButton
                  size="medium"
                  sx={PLAY_BTN_STYLE}
                  onClick={e => {
                    e.stopPropagation();
                    handleScheduleActivate();
                  }}
                  title="Activate">
                  <PlayArrowIcon fontSize="16px" />
                </IconButton>
              )}

              {(!['active', 'active_not_synced'].includes(props.status) || !isActiveForAssignedDevice) && (
                <IconButton
                  size="medium"
                  sx={{
                    border: `1px solid ${brandColors.GREY}`,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    handleScheduleDelete(props.scheduleId, props.scheduleName);
                  }}
                  title="Delete">
                  <DeleteIcon fontSize="16px" />
                </IconButton>
              )}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            position: 'absolute',
            top: -17,
            right: -10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1.7,
            height: '1rem',
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            lineHeight: 1.5,
            textAlign: 'center',
            pointerEvents: 'none',
            borderRadius: 1,
            backgroundColor: props.is_global ? '#0d47a1' : brandColors.GREY_DARK,
          }}>
          {props.is_global ? (
            <PublicIcon sx={{ scale: '1.8', color: '#fff' }} />
          ) : (
            <SensorsIcon sx={{ scale: '2.2', color: '#fff', marginLeft: '.2rem' }} />
          )}
          <MDTypography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '0.5px',
              marginLeft: '.6rem',
            }}>
            {globalLabel}
          </MDTypography>
        </Box>
      </Card>
      <Dialog open={showDeviceModal} onClose={() => setShowDeviceModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Select devices for schedule: "{props.scheduleName}"</DialogTitle>

        <DialogContent sx={{ minHeight: '400px' }}>
          {assignedControllers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>No assigned devices available</Typography>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {assignedControllers.map(controller => (
                <Grid item xs={12} sm={6} md={4} key={controller.id}>
                  <Box
                    onClick={() => toggleDeviceSelection(controller.id)}
                    sx={{
                      position: 'relative',
                      opacity: selectedDevices.includes(controller.id) ? 1 : 0.6,
                      transition: 'opacity 0.2s',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 1,
                      },
                    }}>
                    {/* Checkbox overlay */}
                    <Checkbox
                      checked={selectedDevices.includes(controller.id)}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: -12,
                        zIndex: 1,
                        backgroundColor: 'white',
                        borderRadius: '4px',
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        toggleDeviceSelection(controller.id);
                      }}
                    />

                    <ControllerCard
                      className="controllerCard"
                      id={controller.id}
                      name={controller.name}
                      deviceType={controller.device_type}
                      controller_model={controller.controller_model}
                      comfort={controller.comfort}
                      building_id={controller.building_id}
                      building={controller.building_name}
                      floorNumber={controller.floor_number}
                      area_name={controller.area_name}
                      params={{
                        last_record_time: controller.latest_controller_data?.last_record_time,
                        on_off: controller.latest_controller_data?.on_off,
                        control: controller.latest_controller_data?.control,
                        mode: controller.latest_controller_data?.mode,
                        cool_water_valve: controller.latest_controller_data?.cool_water_valve,
                        temp_set_point: controller.latest_controller_data?.temp_set_point,
                        fan_speed: controller.latest_controller_data?.fan_speed,
                      }}
                      displayData={true}
                      onCardClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleDeviceSelection(controller.id);
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selectedDevices.length} device(s) selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => {
                  setShowDeviceModal(false);
                  setSelectedDevices([]);
                }}>
                Cancel
              </Button>
              <Button onClick={handleConfirmActivation} disabled={isActivating} variant="contained">
                {isActivating ? <CircularProgress size={24} /> : 'Activate on selected devices'}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Activate Schedule</DialogTitle>
        <DialogContent>
          <Typography>
            are you shure you want to activate this schedule on device :{' '}
            {primaryDevice
              ? `${primaryDevice.name} (${primaryDevice.building} / Floor ${primaryDevice.floor} / ${primaryDevice.area})`
              : 'Unknown device'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setShowConfirmModal(false);
            }}>
            No
          </Button>
          <Button onClick={handleConfirmSingleActivation} variant="contained" disabled={isActivating}>
            {isActivating ? <CircularProgress size={24} /> : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={4000}
        vertical="top"
        horizontal="right"
        color="#fff"
      />
    </>
  );
};

export default ScheduleCard;
